/**
 * ColumnStats.jsx — Per-column enrichment fill % progress bars.
 * Shows how many documents have each field populated vs total enriched.
 */

import { memo } from "react";

const FIELD_META = [
  { key: "title",         label: "Title",               color: "#1E90FF" },
  { key: "pub_date",      label: "Publication Date",    color: "#60A5FA" },
  { key: "summary",       label: "Summary",             color: "#1987C9" },
  { key: "category",      label: "Primary Category",    color: "#1987C9" },
  { key: "secondary_cat", label: "Secondary Category",  color: "#0E6BA8" },
  { key: "subtopics",     label: "Subtopics",           color: "#0E6BA8" },
  { key: "companies",     label: "Companies Mentioned", color: "#F59E0B" },
  { key: "use_cases",     label: "Use Cases",           color: "#F59E0B" },
  { key: "grant",         label: "Grant / Funding",     color: "#0F7E6D" },
  { key: "bd",            label: "BD Priority",         color: "#DB2777" },
  { key: "sourcing",      label: "Sourcing Priority",   color: "#DB2777" },
  { key: "tech",          label: "Tech Theme",          color: "#9333EA" },
  { key: "operators",     label: "Operators Mentioned", color: "#9333EA" },
];

export default memo(function ColumnStats({ stats = {}, enrichedTotal = 0 }) {
  if (!enrichedTotal) return null;

  return (
    <section className="hm-section">
      <div className="hm-section-header">
        <h2 className="hm-section-title">Enrichment Coverage</h2>
        <span className="hm-section-badge">{enrichedTotal.toLocaleString()} docs enriched</span>
        <span style={{ marginLeft: 8, fontSize: 12, color: "var(--text-tertiary)" }}>
          Field fill rates across all enriched documents
        </span>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        gap: "12px 20px",
        padding: "16px 20px",
      }}>
        {FIELD_META.map(({ key, label, color }) => {
          const s = stats[key] || { count: 0, pct: 0 };
          return (
            <FieldBar
              key={key}
              label={label}
              count={s.count}
              pct={typeof s.pct === "number" ? s.pct : 0}
              total={enrichedTotal}
              color={color}
            />
          );
        })}
      </div>
    </section>
  );
});

function FieldBar({ label, count, pct, total, color }) {
  const displayPct = Math.min(100, Math.max(0, pct));
  const tier = displayPct >= 80 ? "high" : displayPct >= 40 ? "mid" : "low";
  const tierColor = tier === "high" ? color : tier === "mid" ? "#F59E0B" : "#9CA3AF";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>
          {label}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: tierColor }}>
          {displayPct.toFixed(1)}%
        </span>
      </div>

      {/* progress bar */}
      <div style={{
        height: 6,
        background: "var(--surface-secondary)",
        borderRadius: 3,
        overflow: "hidden",
      }}>
        <div style={{
          height: "100%",
          width: `${displayPct}%`,
          background: tierColor,
          borderRadius: 3,
          transition: "width 0.5s ease",
        }} />
      </div>

      <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
        {count.toLocaleString()} / {total.toLocaleString()} filled
      </div>
    </div>
  );
}
